import React from 'react';

type Brand<K, T> = K & { __brand: T };
export type DayNumber = Brand<string, 'DayArg'>;
// format YYYY-MM-DD
export type SimpleDate = Brand<string, 'SimpleDate'>;
export type DayParam = DayNumber | SimpleDate;

export type GameType = 'fields' | 'stars' | 'loopy-river' | 'mosaic';

export type UserRole = 'user' | 'friend' | 'influencer' | 'puzzle_creator' | 'admin';

export type DomainUser = {
  user_str: string | undefined;
  auth: { email?: string; id: string } | undefined;
  role: UserRole;
};

/**
 * What a mark on a cell *means*, which is the only thing the game logic and the tutorials get to say.
 * How it looks is the grid's business, and differs per game and per platform.
 */
export enum MarkedCellType {
  /** Look here: the evidence a hint or a tutorial step is reasoning from. Not interactive. */
  reference = 'reference',
  highlight = 'highlight',
  error = 'error',
  /**
   * Outside the step: dimmed, and not interactive. A grid stops taking input on a complete board
   * anyway, so a finished tutorial says nothing rather than marking every cell.
   */
  disabled = 'disabled',
  edgeRight = 'edgeRight',
  edgeDown = 'edgeDown',
  /**
   * Hint: this cell takes the game's positive mark — a filled cell in Mosaic, a star in Stars. Fields
   * has two, so `markData` names the colour: 'blue' or 'green'.
   */
  hintFill = 'hintFill',
  /** Hint: this cell takes the game's negative mark — an ✕. */
  hintCross = 'hintCross',
  /** Its mark drawn faint: the backdrop to a tutorial demo, behind the cells the demo is about. */
  faded = 'faded',
}

/**
 * Names an icon to draw in a marked cell. A name rather than a rendered element: hint generation is
 * pure logic shared across renderers, so it must not reach for JSX. The renderer resolves the name.
 */
export type MarkIcon = 'star-outline' | 'no-star';

export interface MarkedCell {
  row: number;
  col: number;
  markType: MarkedCellType;
  markData?: string;
  markIcon?: MarkIcon;
}

export enum BoardMessageType {
  success = 'success',
  error = 'error',
  hint = 'hint',
  tutorial = 'tutorial',
}

export interface BoardMessage {
  type: BoardMessageType;
  message: React.ReactNode;
  markedCells: MarkedCell[];
  /** Optional game-specific attributes (like 'show checkerboard' for Loopy River). */
  attrs?: string[];
}

export interface PlayStats {
  hintsUsed: number;
  checksUsed: number;
  /** Number of times the play was paused and resumed. Optional: absent on plays persisted before
   * this counter existed, defaulted to 0 on read. */
  pauseCount?: number;
}

// [row, col, previousValue]
export type CellChange = [number, number, unknown];
// [playDataKey, previousValue]: a play-data field other than the cells, such as Mosaic's ticked-off
// clues. A previous value of undefined is stored (and restored) as null, which reads as absent.
export type FieldChange = [string, unknown];
// A single move can affect one or more cells, and any other play-data fields alongside them.
export type MoveAction = (CellChange | FieldChange)[];

export const isCellChange = (change: CellChange | FieldChange): change is CellChange => typeof change[0] === 'number';

export type PlayCompletion = {
  completionTime: number;
  completionHintsUsed: number;
  completionChecksUsed: number;
  completedAt: Date;
};

/** What the hint box is showing: a check's result, a hint, or a `tip` that neither button asked for. */
export type HintBoxState = 'none' | 'check' | 'hint' | 'tip';

/**
 * Describes a single highlight mode a game can offer.
 * `color` is any CSS color value; `label` is used for the button aria-label.
 */
export interface HighlightConfig {
  color: string;
  label: string;
}

export interface PlayData<CellT> {
  cells: CellT[][];
  playStats: PlayStats;
  /** Client wall-clock time (epoch ms) at which this blob was persisted — locally or sent to the
   * server. Used to reconcile localStorage vs server copies; absent on legacy data. */
  savedAt?: number;
}

export interface PlayWithExtra<CellT = unknown> {
  id: number;
  board_id: number;
  complete: boolean;
  play_duration: number;
  play_data: PlayData<CellT>;
  winsHistory: PlayCompletion[];
  moveHistory: MoveAction[];
  created_at?: string | null;
}

/** A play as loaded into the play store: PlayWithExtra, except id may be null when the play was
 * seeded from local-only progress that has no DB row yet. */
export type LoadedPlay<CellT = unknown> = Omit<PlayWithExtra<CellT>, 'id'> & { id: number | null };

export interface Board {
  id: number;
  game_id: number;
  difficulty: number;
  name: string;
  date: SimpleDate;
  solution: any;
  /** Slug for non-daily boards (tutorial / practice / intro puzzles); absent for daily boards. */
  slug?: string;
  /** The Monday of a weekly challenge board's week; absent for every other board. */
  challengeWeek?: SimpleDate;
}
